import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors with one silent refresh retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const url = originalRequest.url || '';

    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout');

    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        try {
          const refreshResponse = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token, user } = refreshResponse.data;

          localStorage.setItem('token', access_token);

          if (refresh_token) {
            localStorage.setItem('refresh_token', refresh_token);
          }

          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
          }

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${access_token}`;

          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: (refreshToken) => api.post('/auth/logout', { refresh_token: refreshToken }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { 
    current_password: currentPassword, 
    new_password: newPassword 
  }),
  updateTheme: (theme) => api.patch('/auth/theme', { theme }),
  updateProfile: (data) => api.patch('/auth/profile', data),
};

// Staff Management API
export const staffAPI = {
  create: (data) => api.post('/staff', data),
  getAll: (params) => api.get('/staff', { params }),
  getById: (id) => api.get(`/staff/${id}`),
  update: (id, data) => api.patch(`/staff/${id}`, data),
  updateStatus: (id, isActive) => api.patch(`/staff/${id}/status`, { is_active: isActive }),
  delete: (id) => api.delete(`/staff/${id}`),
};

// Clients API
export const clientsAPI = {
  create: (data) => api.post('/clients', data),
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Visits API
export const visitsAPI = {
  create: (data) => api.post('/visits', data),
  getAll: (params) => api.get('/visits', { params }),
  getMyNext: () => api.get('/visits/my-next'),
  getMyUpcoming: () => api.get('/visits/my-upcoming'),
  getMyPast: () => api.get('/visits/my-past'),
  getById: (id) => api.get(`/visits/${id}`),
  update: (id, data) => api.put(`/visits/${id}`, data),
  complete: (id) => api.post(`/visits/${id}/complete`),
  delete: (id) => api.delete(`/visits/${id}`),
  deleteAll: () => api.delete('/visits/bulk/all'),
};

// Travel API
export const travelAPI = {
  start: (visitId, fromLocation) => api.post('/travel/start', { visit_id: visitId, from_location: fromLocation }),
  arrived: (travelId, toLocation, distanceKm) => api.post('/travel/arrived', { 
    travel_id: travelId, 
    to_location: toLocation,
    distance_km: distanceKm 
  }),
  getAll: (params) => api.get('/travel', { params }),
};

// Clock API
export const clockAPI = {
  clockOn: (visitId, gpsLocation) => api.post('/clock/on', { visit_id: visitId, gps_location: gpsLocation }),
  clockOff: (clockId, gpsLocation, shiftNotes, endOdometer) =>
    api.post('/clock/off', {
      clock_id: clockId,
      gps_location: gpsLocation || null,
      shift_notes: shiftNotes || '',
      notes: shiftNotes || '',
      end_odometer: endOdometer ?? null
    }),
  getAll: (params) => api.get('/clock', { params }),
  getActive: () => api.get('/clock/active'),
};

// Notes API
export const notesAPI = {
  create: (data) => api.post('/notes', {
    ...data,
    note_text: data.note_text || data.notes || data.note || ''
  }),
  getAll: (params) => api.get('/notes', { params }),
};

// Timesheets API
export const timesheetsAPI = {
  generate: (weekStart) => api.get('/timesheet-declarations/generate', { params: { week_start: weekStart } }),
  getAll: (params = {}) => api.get('/timesheet-declarations', { params }),
  submit: (id) => api.post(`/timesheet-declarations/${id}/submit`),
  approve: (id, approved, rejectionReason) => api.post(`/timesheet-declarations/${id}/approve`, { 
    approved, 
    rejection_reason: rejectionReason 
  }),
};

// Mileage API
export const mileageAPI = {
  create: (data) => api.post('/mileage', data),
  update: (id, data) => api.patch(`/mileage/${id}`, data),
  getAll: (params) => api.get('/mileage', { params }),
  getActive: () => api.get('/mileage/active'),
  getSummary: (params) => api.get('/mileage/summary', { params }),
  approve: (id, approved, rejectionReason) => api.post(`/mileage/${id}/approve`, { 
    approved, 
    rejection_reason: rejectionReason 
  }),
  bulkApprove: (mileageIds) => api.post('/mileage/bulk-approve', mileageIds),
  exportXero: (params) => api.post('/mileage/export/xero', null, { 
    params,
    responseType: 'blob'
  }),
  previewExport: (params) => api.get('/mileage/export/preview', { params }),
};

// Export API
export const exportAPI = {
  timesheetsCSV: (weekStart) => api.get('/export/timesheets/csv', { 
    params: { week_start: weekStart },
    responseType: 'blob'
  }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
};


// Audit Trail API
export const auditAPI = {
  all: (limit = 100) => api.get('/audit-trail', { params: { limit } }),
  security: (limit = 100) => api.get('/audit-trail/security', { params: { limit } }),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

export default api;


// Participants / NDIS API
export const participantsAPI = {
  getAll: (params = {}) => api.get('/participants', { params }),
  getById: (id) => api.get(`/participants/${id}`),
  create: (data) => api.post('/participants', data),
  update: (id, data) => api.put(`/participants/${id}`, data),
  delete: (id) => api.delete(`/participants/${id}`),
  summary: () => api.get('/participants-dashboard/summary'),
};


// Geocoding API
export const geocodeAPI = {
  find: (address) => api.get('/geocode', { params: { address } }),
  suggestions: (address) => api.get('/address-suggestions', { params: { address } }),
};


export const shiftReportsAPI = {
  getAll: () => api.get('/reports/shifts'),
  delete: (clockId) => api.delete(`/reports/shifts/${clockId}`),
};
